import { LitElement, html, css, unsafeCSS, render as renderIntoContainer } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import cookies from 'js-cookie';

import config from '../../../shared/config/config';
import analytics, { events } from '../../../shared/analytics';
import { getCID } from '../../utils/installer-url/installer-url';
import Logger from '../../utils/logger/logger';
import snapScrollToBottom from '../../utils/snap-scroll-to-bottom';
import SignupModal from '../signup-modal/signup-modal';

import appRecommendations from './partials/app-recommendations';
import rateAnswerBlock from './partials/rate-answer';

import aiIcon from './images/assistant-icon.svg';
import closeIcon from './images/close-icon.svg';

import styles from './web-assistant-popup.component.scss';

type MessageType = 'user' | 'bot';

const REFRESH_TOKEN_NAME = 'customer_refresh_token';

class WebAssistantPopup extends LitElement {
  @state() textareaValue = '';
  @state() isStreamingInProgress = false;
  @state() isOpened = true;
  @state() openedForFirstTime = true;
  @query('.js-list') messagesList: HTMLDivElement | undefined;
  @property({ type: Boolean, attribute: 'is-logged-in' }) isLoggedIn = false;

  appNamesRegex = '';
  textDecoder = new TextDecoder();
  signupModal = new SignupModal();
  lastBotText: HTMLDivElement | undefined;
  stopSnappingScroll = () => {};
  stopObservingMessages = () => {};

  static styles = css`${unsafeCSS(styles.toString())}`;

  async connectedCallback() {
    super.connectedCallback();

    fetch('/ajax/application-names').then((res) => res.json()).then((json) => {
      this.appNamesRegex = json['data'];
    });
  }

  firstUpdated() {
    if (!this.messagesList) {
      return;
    }

    const { updateScroll, destroy } = snapScrollToBottom(this.messagesList);

    const observerForScroll = new MutationObserver(() => {
      // mutations for message list is any change to child nodes or their text content
      // so we can scroll on every change as it changes size
      updateScroll();
    });

    observerForScroll.observe(this.messagesList, {
      childList: true,
      subtree: true,
    });

    this.stopSnappingScroll = destroy;
    this.stopObservingMessages = () => observerForScroll.disconnect();

    // wait for starter animation to finish
    setTimeout(() => {
      this.openedForFirstTime = false;
    }, 2000);
  }

  disconnectedCallback() {
    this.stopSnappingScroll();
    this.stopObservingMessages();

    super.disconnectedCallback();
  }

  messageItemTemplate(message: string, type: MessageType = 'bot') {
    const BOT_MESSAGE_CLASS = 'assistant-popup__message-item_bot';
    const USER_MESSAGE_CLASS = 'assistant-popup__message-item_user';

    const messageContainer = document.createElement('div');
    const messageText = document.createElement('div');

    messageContainer.classList.add('assistant-popup__message-container');
    messageText.classList.add('assistant-popup__message-item');
    messageText.classList.add(type === 'bot' ? BOT_MESSAGE_CLASS : USER_MESSAGE_CLASS);

    messageText.textContent = message;

    messageContainer.appendChild(messageText);

    return { messageContainer, messageText };
  }

  handleTextareaInput(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    this.textareaValue = textarea.value;
  }

  handleTextareaKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      this.handleSubmitClick();
    }
  }

  handleRecommendationClick(url: string, name: string) {
    analytics.trackEvent({
      ...events.WEB_ASSISTANT_CLICK_RECOMMENDED,
      eventLabel: 'Try Free',
      eventLabel2: name,
    });

    if (!this.isLoggedIn) {
      this.signupModal.open();

      return;
    }

    window.open(url);
  }

  handleOpenClick() {
    this.isOpened = true;

    analytics.trackEvent({
      ...events.WEB_ASSISTANT_OPEN,
      eventLabel: 'right_bottom',
    });
  }

  handleCloseClick() {
    this.isOpened = false;

    analytics.trackEvent(events.WEB_ASSISTANT_CLOSE);
  }

  async handleSubmitClick({ predefined = false } = {}) {
    if (!this.textareaValue || !this.messagesList) {
      return;
    }

    const userInput = this.textareaValue;

    this.textareaValue = '';

    const startTime = Date.now();

    analytics.trackEvent({
      ...events.WEB_ASSISTANT_QUERY,
      eventLabel: userInput,
      ...(predefined && { eventLabel2: 'predefined' }),
    });

    const {
      messageContainer: userMessageContainer,
    } = this.messageItemTemplate(userInput, 'user');

    this.messagesList.appendChild(userMessageContainer);

    this.isStreamingInProgress = true;

    const { generatedText, startResponseTimestamp } = await this.processUserMessage(userInput);

    this.isStreamingInProgress = false;

    const { recommendedAppIds, recommendedApps } = await this.getAppRecommendations(generatedText);

    const responseDuration = Date.now() - startTime;
    const responseStartDuration =  startResponseTimestamp - startTime;

    this.sendAnalyticsToDatahub({
      query: userInput,
      appsList: recommendedApps,
      appsListIds: recommendedAppIds,
      response: generatedText,
      responseDuration,
      responseStartDuration,
    });

    analytics.trackEvent(events.WEB_ASSISTANT_RESPONSE_READY);
  }

  async processUserMessage(message: string) {
    const {
      messageContainer: botMessageContainer,
      messageText: botMessageText,
    } = this.messageItemTemplate('', 'bot');

    // don't add bot message immediately
    setTimeout(() => {
      this.messagesList?.appendChild(botMessageContainer);
      this.lastBotText = botMessageText;
    }, 500);

    const response = await fetch('/api/web-assistant', {
      method: 'POST',
      body: JSON.stringify({ input: message }),
      headers: { 'Content-Type': 'application/json' },
    });

    let startResponseTimestamp = Date.now();
    let isResponseTimestampSaved = false;

    if (!response.ok) {
      let errorText = 'Sorry, something went wrong. Please try again later.';

      if (response.headers.get('Content-Type') === 'application/json') {
        const json = await response.json();
        errorText = json['errors']['input'][0] || errorText;
      }

      botMessageText.textContent = errorText;

      return { generatedText: errorText, startResponseTimestamp };
    }

    const reader = response.body?.getReader();

    const processText = async () => {
      const { done, value } = await reader!.read();

      if (done) {
        renderIntoContainer(rateAnswerBlock(this.messagesList!.children.length), botMessageContainer);

        return;
      }

      botMessageText.textContent += this.textDecoder.decode(value);

      if (!isResponseTimestampSaved) {
        isResponseTimestampSaved = true;
        startResponseTimestamp = Date.now();
      }

      await processText();
    };

    try {
      await processText();

      return { generatedText: botMessageText.textContent ?? '', startResponseTimestamp };
    } catch (e) {
      Logger.catchError(e);

      return { generatedText: '', startResponseTimestamp };
    }
  }

  async getAppRecommendations(text: string | undefined) {
    const recommendedApps: string[] = [];
    const recommendedAppIds: string[] = [];

    if (!this.appNamesRegex || !text) {
      return { recommendedAppIds, recommendedApps };
    }

    const regex = new RegExp(this.appNamesRegex, 'g');
    const appNameMatches = new Set(Array.from(text.matchAll(regex)).map((match) => match[0]));

    const appRecommendationsUrl = new URLSearchParams();

    let index = 0;
    appNameMatches.forEach((appName) => {
      appRecommendationsUrl.set(`q[${index}]`, appName);
      index++;
    });

    if (appNameMatches.size === 0) {
      return { recommendedAppIds, recommendedApps };
    }

    try {
      const response = await fetch(`/ajax/application-previews?${appRecommendationsUrl.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to get app recommendations for web assistant: ${response.statusText}`);
      }

      const appData = (await response.json()).data;

      let textWithLinks = text;

      appData.forEach((app: { name: string; url: string; id: string }) => {
        recommendedApps.push(app.name);
        recommendedAppIds.push(app.id);

        textWithLinks = textWithLinks.replaceAll(app.name, (match) => {
          return `<a
            class="assistant-popup__link js-app-link"
            href="${app.url}"
            target="_blank"
            rel="noopener noreferrer"
          >${match}</a>`;
        });
      });

      this.lastBotText!.innerHTML = textWithLinks;

      this.lastBotText?.querySelectorAll('.js-app-link').forEach((link) => {
        link.addEventListener('click', () => {
          analytics.trackEvent({
            ...events.WEB_ASSISTANT_CLICK_RECOMMENDED,
            eventLabel: 'Link',
            eventLabel2: link.textContent,
          });
        });
      });

      // appends recommendations inside last bot message
      renderIntoContainer(
        appRecommendations(appData, this.handleRecommendationClick.bind(this)),
        this.lastBotText!
      );

      return { recommendedAppIds, recommendedApps };
    } catch (e) {
      Logger.catchError(e);

      return { recommendedAppIds, recommendedApps };
    }
  }

  handleSuggestionClick(suggestion: string) {
    if (this.isStreamingInProgress) {
      return;
    }

    this.textareaValue = suggestion;
    this.handleSubmitClick({ predefined: true });
  }

  sendAnalyticsToDatahub({ query, response, responseDuration, responseStartDuration, appsList, appsListIds }) {
    const refreshToken = cookies.get(REFRESH_TOKEN_NAME);

    const userId = refreshToken ? refreshToken.split('|')[0] : null;

    try {
      analytics.insertDatahubData('assistant_queries', {
        cid: getCID(),
        source: 'web_assistant',
        platform: 'web',
        query,
        response,
        'response_duration': responseDuration,
        'response_start_duration': responseStartDuration,
        'apps_list': appsList,
        'apps_list_ids': appsListIds,
        ...(Boolean(userId) && { 'setapp_uid': userId }),
      });
    } catch (e) {
      Logger.catchError(e);
    }
  }

  render() {
    const suggestions = [
      'Сhange video format',
      'Best photo editor',
      'My Mac is slow',
      'Take better screenshots',
      'How to keep track of my budget',
    ];

    return html`
      <div class="assistant-popup 
        ${this.isOpened ? '' : 'assistant-popup_hidden'} 
        ${this.openedForFirstTime ? 'assistant-popup_first-open' : ''}">
        <button class="assistant-popup__close-btn">
          <img
            @click="${this.handleCloseClick}"
            src="${closeIcon}"
            width="28"
            height="28"
            alt="Close"
          />
        </button>
        <div class="assistant-popup__list js-list">
          <div class="assistant-popup__message-container">
            <div class="assistant-popup__message-item assistant-popup__header-message">👋</div>
          </div>

          <div class="assistant-popup__message-container">
            <div class="assistant-popup__message-item assistant-popup__header-message">
              Hi, I’m your Setapp <span class="assistant-popup__decorated-word">Assistant</span>
            </div>
          </div>

          <div class="assistant-popup__message-container">
            <div class="assistant-popup__message-item">
              I can find apps, solve tasks, and give tips on using Setapp on your Mac. Choose one of the options below or ask me anything.
            </div>
          </div>

          <div class="assistant-popup__message-container">
            <div class="assistant-popup__message-item">
              What are you looking for?
              <ul class="assistant-popup__suggestions">
                ${suggestions.map((suggestion) => html`
                  <li
                    class="assistant-popup__suggestion-item"
                    @click="${() => this.handleSuggestionClick(suggestion)}"
                  >
                    ${suggestion}
                  </li>
                `)}
              </ul>
            </div>
          </div>
        </div>

        <div class="assistant-popup__input-container">
          <div class="assistant-popup__input">
            <img
              src="${aiIcon}"
              class="assistant-popup__icon"
              width="16"
              height="16"
              alt=""
            />
            <span
              class="assistant-popup__textarea-container"
              data-value="${this.textareaValue}"
            >
              <textarea
                @input="${this.handleTextareaInput}"
                @keydown="${this.handleTextareaKeydown}"
                .value="${this.textareaValue}"
                ?disabled="${this.isStreamingInProgress}"
                class="assistant-popup__textarea"
                placeholder="Ask me about app or task"
                rows="1"
              ></textarea>
            </span>
            <button
              @click="${this.handleSubmitClick}"
              ?disabled="${this.isStreamingInProgress}"
              class="btn btn_primary btn_md assistant-popup__submit-btn"
            >
              Ask me
            </button>
          </div>
        </div>
      </div>
      <div 
        @click="${this.handleOpenClick}"
        class="assistant-popup assistant-popup_closed-state ${this.isOpened ? 'assistant-popup_hidden' : ''}"
      >
        <span class="assistant-popup__closed-header">
          <img
            src="${aiIcon}"
            class="assistant-popup__icon"
            width="16"
            height="16"
            alt=""
          />
          <p class="assistant-popup__closed-title">
            Setapp Assistant
          </p>
        </span>
        <div class="assistant-popup__message-item assistant-popup__closed-text">
          Ask questions about Mac, search apps, and more.
        </div>
        <button class="btn btn_md btn_primary">
          Open Chat
        </button>
      </div>
    `;
  }
}

const defineWebAssistantPopup = () => {
  customElements.define(config.get('customElements').webAssistantPopup, WebAssistantPopup);
};

export default defineWebAssistantPopup;
